import { Link } from "react-router-dom";
import {
  Search,
  BarChart3,
  FileSignature,
  Handshake,
  ShieldCheck,
  Scale,
  UserCheck,
  TrendingDown,
  ArrowRight,
} from "lucide-react";
import { BRAND, ADVANTAGES } from "../data/brand";

const ADV_ICONS = { ShieldCheck, Scale, UserCheck, TrendingDown };

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Analisi delle esigenze",
    body: "Partiamo dal tuo profilo: abitudini, patrimonio, famiglia o attività. Capiamo cosa proteggere davvero e dove si nascondono i rischi reali.",
  },
  {
    n: "02",
    icon: BarChart3,
    title: "Confronto tra compagnie",
    body: "Interroghiamo il mercato e selezioniamo le soluzioni migliori per rapporto qualità-prezzo, evidenziando differenze di garanzie ed esclusioni.",
  },
  {
    n: "03",
    icon: FileSignature,
    title: "Consulenza e scelta",
    body: "Ti presentiamo due o tre alternative chiaramente spiegate. Scegli tu, in piena consapevolezza, senza pressioni commerciali.",
  },
  {
    n: "04",
    icon: Handshake,
    title: "Assistenza continua",
    body: "Dopo la firma restiamo al tuo fianco: ti aiutiamo nei sinistri, nei rinnovi e nelle modifiche, con revisione annuale gratuita.",
  },
];

export default function ComeFunziona() {
  return (
    <div data-testid="come-funziona-page">
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="container-90 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-6">Come funziona</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
                Il broker assicurativo,
                <br />
                <span className="text-[color:var(--brand-blue)]">spiegato semplice.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-slate-600 leading-relaxed">
                Non siamo agenti di una compagnia. Siamo professionisti che lavorano per te:
                confrontiamo, negoziamo e gestiamo le tue polizze in piena indipendenza.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Steps */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Il nostro processo</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Quattro passaggi,
              <br />
              <span className="text-[color:var(--brand-blue)]">zero sorprese.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={i}
                  data-testid={`step-${i + 1}-card`}
                  className={`p-8 lg:p-10 bg-white hover:bg-slate-50 transition-colors ${
                    i < STEPS.length - 1 ? "md:border-r border-slate-200" : ""
                  } ${i < 2 ? "md:border-b lg:border-b-0 border-slate-200" : ""}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-8 h-8 text-[color:var(--brand-blue)]" strokeWidth={1.5} />
                    <span className="font-heading text-5xl font-extrabold text-[color:var(--brand-yellow)]/30">
                      {step.n}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages / Broker vs Agent */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-90">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="overline mb-4">Broker vs. Agente</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05] mb-6">
                Cosa significa davvero{" "}
                <span className="text-[color:var(--brand-blue)]">essere indipendenti.</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Il broker assicurativo è l'unico intermediario che non ha mandato da alcuna
                compagnia. Questo significa che lavora esclusivamente nel tuo interesse,
                confrontando tutto il mercato.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-6">
              {ADVANTAGES.map((a, i) => {
                const Icon = ADV_ICONS[a.icon] || ShieldCheck;
                return (
                  <div
                    key={a.id}
                    data-testid={`come-advantage-${a.id}`}
                    className="bg-white p-8 border border-slate-200 hover:border-[color:var(--brand-blue)] transition-colors flex gap-6"
                  >
                    <div className="flex-shrink-0 w-14 h-14 bg-[color:var(--brand-blue)] text-white flex items-center justify-center">
                      <Icon className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-heading font-bold text-slate-400">
                          0{i + 1}
                        </span>
                        <h3 className="font-heading text-xl font-bold text-slate-900">
                          {a.title}
                        </h3>
                      </div>
                      <p className="text-slate-600 leading-relaxed">{a.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ-style trust */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline mb-4">Domanda frequente</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
              "Cosa succede in caso di sinistro?"
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              Ti seguiamo <strong>dal primo istante fino alla liquidazione</strong>: apriamo
              noi la pratica con la compagnia, raccogliamo la documentazione necessaria e
              gestiamo le comunicazioni al tuo posto. Tu ti concentri su ciò che conta, noi
              sul resto.
            </p>
            <p className="text-slate-500 text-sm">
              Un unico referente dedicato per tutta la durata del sinistro: nessun call center,
              nessun passaggio di mano.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0f1f] text-white py-24">
        <div className="container-90 text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-6">
            Pronto a scoprire quanto puoi migliorare?
          </h2>
          <p className="text-slate-300 leading-relaxed text-lg mb-10">
            Fissa una consulenza gratuita: ti mostreremo dove risparmiare e dove rafforzare
            le tue coperture.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="comefunziona-call-btn"
              className="btn-primary"
            >
              {BRAND.phone} <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/contatti" data-testid="comefunziona-link-contatti" className="btn-outline" style={{ borderColor: "white", color: "white" }}>
              Tutti i contatti
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
